import React, { useState } from 'react'
import { Checkbox, Modal, RadioGroup } from '@enamelai/design-system'
import './RemindPatientModal.css'

export interface AppointmentSchedule {
  id: string
  date: string
  type: string
}

export interface RemindSendOptions {
  sendThrough: 'mobile' | 'email'
  selectedAppointments: string[]
  includeOption: 'both' | 'otp-only' | 'url-only'
}

interface RemindPatientModalProps {
  show: boolean
  onHide: () => void
  patientName: string
  patientMobile?: string
  patientEmail?: string
  appointments: AppointmentSchedule[]
  onOTPShow?: (patientName: string, otp: string) => void
  /** Called when user confirms send; parent should show global loading then toast (and OTP modal if mobile). */
  onConfirmSend?: (options: RemindSendOptions) => void
}

const INCLUDE_OPTIONS = [
  { value: 'both', label: 'Link to patient forms (URL) and new OTP' },
  { value: 'otp-only', label: 'New OTP only' },
  { value: 'url-only', label: 'Link to patient forms (URL) only' },
]

const RemindPatientModal: React.FC<RemindPatientModalProps> = ({
  show,
  onHide,
  patientMobile,
  patientEmail,
  appointments,
  onConfirmSend,
}) => {
  const [sendThrough, setSendThrough] = useState<'mobile' | 'email'>('mobile')
  const [selectedAppointments, setSelectedAppointments] = useState<string[]>([])
  const [includeOption, setIncludeOption] = useState<
    'both' | 'otp-only' | 'url-only'
  >('both')

  const resetState = (): void => {
    setSendThrough('mobile')
    setSelectedAppointments([])
    setIncludeOption('both')
  }

  const handleAppointmentToggle = (appointmentId: string): void => {
    setSelectedAppointments((prev) =>
      prev.includes(appointmentId)
        ? prev.filter((id) => id !== appointmentId)
        : [...prev, appointmentId],
    )
  }

  const handleSendReminder = (): void => {
    onConfirmSend?.({
      sendThrough,
      selectedAppointments,
      includeOption,
    })
    onHide()
    resetState()
  }

  const handleCancel = (): void => {
    onHide()
    resetState()
  }

  return (
    <Modal
      open={show}
      onClose={handleCancel}
      size="md"
      title="Remind patient to complete their forms"
      className="remind-patient-modal"
      bodyClassName="remind-patient-modal__body"
      secondaryLabel="Cancel"
      primaryLabel="Send reminder"
      primaryDisabled={selectedAppointments.length === 0}
      onSecondaryClick={handleCancel}
      onPrimaryClick={handleSendReminder}
    >
      <RadioGroup
        name="remind-send-through"
        label="Send reminder through:"
        orientation="horizontal"
        className="remind-patient-modal__group"
        value={sendThrough}
        onChange={(value) => setSendThrough(value as 'mobile' | 'email')}
        options={[
          {
            value: 'mobile',
            label: `Patient's mobile${patientMobile ? ` (${patientMobile})` : ''}`,
          },
          {
            value: 'email',
            label: `Patient's email${patientEmail ? ` (${patientEmail})` : ''}`,
          },
        ]}
      />

      <fieldset className="remind-patient-modal__section">
        <legend className="remind-patient-modal__label">
          Select appointment schedules:
        </legend>
        <div className="remind-patient-modal__checkboxes">
          {appointments.map((appointment) => (
            <Checkbox
              key={appointment.id}
              id={`remind-appointment-${appointment.id}`}
              label={`${appointment.date} - ${appointment.type}`}
              checked={selectedAppointments.includes(appointment.id)}
              onChange={() => handleAppointmentToggle(appointment.id)}
            />
          ))}
        </div>
      </fieldset>

      <div className="remind-patient-modal__section">
        <RadioGroup
          name="remind-include-option"
          label="Include the following:"
          orientation="vertical"
          className="remind-patient-modal__group"
          value={includeOption}
          onChange={(value) =>
            setIncludeOption(value as 'both' | 'otp-only' | 'url-only')
          }
          options={INCLUDE_OPTIONS}
        />
      </div>
    </Modal>
  )
}

export default RemindPatientModal
